/**
 * Devolução de venda: documento de negócio (estoque volta à fábrica) separado da nota fiscal.
 * Venda com nota autorizada exige NF-e na devolução (emissão própria ou a do cliente).
 * Nenhum crédito ou baixa financeira automática.
 */
import {
  formatMoney,
  type Page,
  type ReturnableLine,
  type SalesOrder,
  type SalesReturn,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ProgressSteps } from "@/components/patterns";
import { apiFetch } from "@/lib/api";
import { toDecimal } from "@/lib/decimal";
import { Pager, SaveError } from "../cadastros/widgets";
import { dateBR, qty as fmtQty, useDocAction } from "../estoque/lib";
import { returnQuantityError } from "./return-lib";
import { EmissionPanel } from "./EmissionPanel";

const KEY = ["vendas", "devolucoes"];
const MODE_LABEL = {
  emissao_propria: "NF-e de devolução emitida pela fábrica",
  nota_do_cliente: "NF-e de devolução do cliente (pela chave)",
  sem_documento_fiscal: "Sem documento fiscal (só se a venda não teve nota)",
} as const;

export function DevolucoesPage() {
  const can = useCan();
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({
    queryKey: [...KEY, offset],
    queryFn: () => apiFetch<Page<SalesReturn>>(`/vendas/devolucoes?limit=50&offset=${offset}`),
  });
  return (
    <div className="space-y-6">
      <PageTitle
        title="Devoluções de venda"
        description="A mercadoria volta ao estoque da fábrica pela devolução; a nota fiscal apenas documenta. Nada de crédito ou baixa automática no financeiro."
        actions={
          can("vendas.devolucao.registrar") && (
            <Button onClick={() => setCreating(true)}>Nova devolução</Button>
          )
        }
      />
      {creating && (
        <NewReturn
          onClose={() => setCreating(false)}
          onDone={(id) => {
            setCreating(false);
            setOpen(id);
          }}
        />
      )}
      {open && <ReturnDetail id={open} onClose={() => setOpen(null)} />}
      <Card>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={[
                "Número",
                "Data",
                "Venda",
                "Cliente",
                "Total",
                "Tratamento fiscal",
                "Situação",
                "",
              ]}
            >
              {list.data?.items.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-mono">{r.number}</td>
                  <td className="px-3 py-2">{dateBR(r.returnDate)}</td>
                  <td className="px-3 py-2">{r.salesOrderNumber}</td>
                  <td className="px-3 py-2">{r.customerName}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(r.totalAmount)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.fiscalMode ? MODE_LABEL[r.fiscalMode] : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      tone={
                        r.status === "confirmado"
                          ? "ok"
                          : r.status === "rascunho"
                            ? "warn"
                            : "neutral"
                      }
                    >
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(r.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function NewReturn({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState<Record<string, string>>({});
  const orders = useQuery({
    queryKey: ["vendas", "pedidos", "devolucao", q],
    queryFn: () =>
      apiFetch<Page<SalesOrder>>(
        `/vendas/pedidos?status=confirmado&limit=20&q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length >= 2 && !order,
  });
  const lines = useQuery({
    queryKey: ["vendas", "devolvivel", order?.id],
    queryFn: () => apiFetch<ReturnableLine[]>(`/vendas/pedidos/${order!.id}/devolvivel`),
    enabled: order !== null,
  });
  const keyOf = (l: ReturnableLine) => `${l.salesOrderItemId}|${l.lotId ?? ""}`;
  const create = useDocAction<Record<string, unknown>, SalesReturn>({
    path: () => "/vendas/devolucoes",
    invalidate: [KEY],
    onDone: (r) => onDone(r.id),
  });
  const errors = Object.fromEntries(
    (lines.data ?? []).map((l) => [
      keyOf(l),
      returnQuantityError(qty[keyOf(l)] ?? "", l.returnable),
    ]),
  );
  const chosen = (lines.data ?? []).filter((l) => Number(toDecimal(qty[keyOf(l)] ?? "")) > 0);
  const invalid = Object.values(errors).some((e) => e !== null);
  const stage = !order ? 0 : chosen.length === 0 ? 1 : reason.trim().length < 3 ? 2 : 3;
  return (
    <Card title="Nova devolução">
      <div className="space-y-4">
        <ProgressSteps
          label="Etapas da devolução"
          steps={["Venda", "Itens e quantidades", "Motivo", "Conferência e rascunho"]}
          current={stage}
        />
        {!order ? (
          <>
            <Field
              label="Venda confirmada (número ou cliente)"
              hint="Digite ao menos 2 letras ou números."
            >
              <Input value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
            <ul className="divide-y divide-border/70 rounded-lg border border-border/70 text-sm empty:hidden">
              {orders.data?.items.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-background"
                    onClick={() => setOrder(o)}
                  >
                    <span>
                      <span className="font-medium">{o.number}</span> — {o.customerName}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatMoney(o.totalAmount)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {orders.data?.items.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma venda confirmada encontrada.</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm">
              Venda <strong>{order.number}</strong> — {order.customerName}{" "}
              <Button size="sm" variant="ghost" onClick={() => setOrder(null)}>
                Trocar
              </Button>
            </p>
            <Table head={["Item", "Lote", "Vendido", "Pode voltar", "Devolver"]}>
              {lines.data?.map((l) => {
                const err = errors[keyOf(l)] ?? null;
                return (
                  <tr key={keyOf(l)}>
                    <td className="px-3 py-2">
                      {l.productCode} {l.productName}
                    </td>
                    <td className="px-3 py-2">{l.lotCode ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtQty(l.sold)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtQty(l.returnable)}</td>
                    <td className="px-3 py-2">
                      <Input
                        className="w-28"
                        inputMode="decimal"
                        aria-label={`Devolver ${l.productCode}${l.lotCode ? ` lote ${l.lotCode}` : ""}`}
                        aria-invalid={err !== null}
                        value={qty[keyOf(l)] ?? ""}
                        onChange={(e) => setQty({ ...qty, [keyOf(l)]: e.target.value })}
                      />
                      {err && <span className="mt-1 block text-xs text-destructive">{err}</span>}
                    </td>
                  </tr>
                );
              })}
            </Table>
            <Field label="Motivo" hint="Obrigatório: por que a mercadoria voltou.">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            {chosen.length > 0 && !invalid && (
              <p className="text-sm text-muted-foreground" role="status">
                Conferência: {chosen.length} item(ns) voltam ao estoque da fábrica quando a
                devolução for confirmada. Agora só o rascunho é criado.
              </p>
            )}
            <div className="flex gap-2">
              <Button
                disabled={create.isPending || invalid || chosen.length === 0}
                onClick={() =>
                  create.mutate({
                    salesOrderId: order.id,
                    reason,
                    items: (lines.data ?? [])
                      .filter((l) => Number(toDecimal(qty[keyOf(l)] ?? "")) > 0)
                      .map((l) => ({
                        salesOrderItemId: l.salesOrderItemId,
                        lotId: l.lotId,
                        quantity: toDecimal(qty[keyOf(l)]!),
                      })),
                  })
                }
              >
                Criar rascunho
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
            </div>
            <SaveError error={create.error} />
          </>
        )}
      </div>
    </Card>
  );
}

function ReturnDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const q = useQuery({
    queryKey: [...KEY, id],
    queryFn: () => apiFetch<SalesReturn>(`/vendas/devolucoes/${id}`),
  });
  const [mode, setMode] = useState<keyof typeof MODE_LABEL>("emissao_propria");
  const [key, setKey] = useState("");
  const [reason, setReason] = useState("");
  const [askReverse, setAskReverse] = useState(false);
  const confirm = useDocAction<Record<string, unknown>, SalesReturn>({
    path: () => `/vendas/devolucoes/${id}/confirmar`,
    invalidate: [KEY],
  });
  const reverse = useDocAction<Record<string, unknown>, SalesReturn>({
    path: () => `/vendas/devolucoes/${id}/estornar`,
    invalidate: [KEY],
  });
  if (!q.data) return <Spinner />;
  const r = q.data;
  return (
    <Card
      title={`Devolução ${r.number} — venda ${r.salesOrderNumber}`}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="space-y-4">
        <ProgressSteps
          label="Andamento da devolução"
          steps={["Rascunho", "Confirmada (no estoque)"]}
          current={r.status === "rascunho" ? 0 : r.status === "confirmado" ? 2 : -1}
          {...(r.status === "estornado" ? { note: "Devolução estornada." } : {})}
        />
        <Table head={["#", "Produto", "Lote", "Quantidade", "Valor"]}>
          {r.items?.map((i) => (
            <tr key={i.id}>
              <td className="px-3 py-2">{i.line}</td>
              <td className="px-3 py-2">
                {i.productCode} {i.productName}
              </td>
              <td className="px-3 py-2">{i.lotCode ?? "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtQty(i.quantity)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatMoney(i.total)}</td>
            </tr>
          ))}
        </Table>
        <p className="text-sm">
          Total {formatMoney(r.totalAmount)} · {r.reason}
        </p>
        {r.status === "rascunho" && can("vendas.devolucao.registrar") && (
          <div className="space-y-2 rounded-md border p-3">
            <Field label="Tratamento fiscal">
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value as keyof typeof MODE_LABEL)}
              >
                {Object.entries(MODE_LABEL).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            {mode === "nota_do_cliente" && (
              <Field label="Chave da NF-e do cliente (44 dígitos)">
                <Input value={key} onChange={(e) => setKey(e.target.value.replace(/\D/g, ""))} />
              </Field>
            )}
            <Button
              disabled={confirm.isPending}
              onClick={() =>
                confirm.mutate({
                  fiscalMode: mode,
                  customerAccessKey: mode === "nota_do_cliente" ? key : null,
                  version: r.version,
                })
              }
            >
              Confirmar (entra no estoque)
            </Button>
            <SaveError error={confirm.error} />
          </div>
        )}
        {r.status === "confirmado" && r.fiscalMode === "emissao_propria" && (
          <EmissionPanel use="devolucao_venda" sourceId={r.id} />
        )}
        {r.status === "confirmado" && can("vendas.devolucao.registrar") && (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Motivo do estorno">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3 || reverse.isPending}
              onClick={() => setAskReverse(true)}
            >
              Estornar devolução
            </Button>
            <SaveError error={reverse.error} />
            <ConfirmDialog
              open={askReverse}
              title={`Estornar a devolução ${r.number}?`}
              confirmLabel="Estornar devolução"
              destructive
              busy={reverse.isPending}
              onCancel={() => setAskReverse(false)}
              onConfirm={() => {
                setAskReverse(false);
                reverse.mutate({ reason, version: r.version });
              }}
            >
              A mercadoria devolvida sai de novo do estoque da fábrica e a devolução fica estornada.
              O estorno não pode ser desfeito.
            </ConfirmDialog>
          </div>
        )}
        {r.status === "estornado" && <Alert tone="info">Estornada: {r.reversalReason}</Alert>}
      </div>
    </Card>
  );
}
